/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import axios, {CancelToken, CancelTokenSource} from 'axios';
import {getAssetCount} from '@ion/core/src/services/ArcGIS';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {getAllAccountIds} from '@ion/core/src/services/FetchWorkerUtils';
import {GenericObject} from '@ion/core/src/types/entities';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {
  TIME_MODE_ASSET_DAY_LIMIT,
  TIMELINE_LIMIT_DAYS
} from '@ion/dls/src/maps/components/timeMode/current/constants';
import {TimeModeLimitWarningProps} from '@ion/dls/src/maps/components/timeMode/current/TimeModeLimitWarning';
import {TimeModes} from '@ion/dls/src/types/types';
import {
  getRunningTimeModeReport,
  isVicinityHistoryReport,
  toRangeDays
} from '../../../routes/tracking2/helpers';
import {getTimeModeLayerCount} from '../../../routes/tracking2/map/containers/store';
import {getReportParams} from '../../../store/helpers';
import {
  globalGroupIdSelector,
  globalGroupStateSelector
} from '../../../store/selectors/global-group-chooser-selectors';
import {
  boundaryFilterSelector,
  boundaryModeSelector,
  historyActiveSelector,
  historyFromSelector,
  historyToSelector,
  liveHistoryActiveSelector,
  timeModeSelector,
  trackingModeSelector
} from '../../../store/selectors/tracking-selectors';
import {TrackingFavoriteFilterState} from './types';

// how long a changed draft is left to settle before its assets are counted,
// so a run of checks and unchecks costs one count rather than one apiece.
// The draft the drawer opens on is counted at once, with nothing to settle ...
export const LIMIT_CHECK_DEBOUNCE = 300;

/**
 * Defines the inputs the Tracking Filter's drafted asset count is taken under.
 */
export interface TrackingFilterAssetCountArgs {
  // the draft's params, as `getReportParams` builds them - `assets`,
  // `groups`, `types` and `categories` are the ones counted ...
  params?: GenericObject;
  globalGroup: GenericObject;
  globalGroupId: number | string;
  // lets a count the draft has since moved past be called off ...
  cancelToken?: CancelToken;
}

/**
 * Counts the assets the Tracking Filter's drafted selection would run a
 * Timeline or History report over, for the current theme's limit warning.
 * Counted the way the History report's own request counts them:
 * - Assets picked out by name are simply counted; the backend is not asked.
 * - Otherwise the backend is asked how many assets the groups, types and
 *   categories select, across every account in the global group when no
 *   group is picked.
 *
 * ```typescript
 * import {getTrackingFilterAssetCount} from './useTrackingFilterLimitWarning';
 *
 * ...
 *
 * const assets = await getTrackingFilterAssetCount({params, globalGroup, globalGroupId});
 * ```
 *
 * @param params        - the draft's params, as `getReportParams` builds them
 * @param globalGroup   - the global group the report runs under
 * @param globalGroupId - the global group's id
 * @param cancelToken   - calls the backend count off once it is no longer wanted
 * @returns The number of assets the drafted selection comes to.
 */
export const getTrackingFilterAssetCount = async({
  params,
  globalGroup,
  globalGroupId,
  cancelToken
}: TrackingFilterAssetCountArgs): Promise<number> => {
  const {
    groups,
    assets     : assetList = [],
    types      : assetTypes,
    categories : assetCategories
  } = params || {};
  let assetCount = assetList.length;
  if (!assetList.length) {
    const {count} = await getAssetCount({
      groups : groups?.length ? groups : getAllAccountIds(globalGroup, entityId(globalGroupId)),
      assets : assetList,
      assetCategories,
      assetTypes
    }, cancelToken);
    assetCount = count;
  }
  return assetCount;
};

// the key a count is kept under - the global group and only the params the
// count is taken over (`assets`, `groups`, `types`, `categories`). The rest -
// the report's dates, its events - play no part in it, and the draft carries
// no dates of its own, so keying by them would keep the draft from ever
// matching a count taken under the applied filters ...
const toCountKey = (globalGroupId: number | string, params?: GenericObject): string => {
  const {assets, groups, types, categories} = params || {};
  return `${globalGroupId}|${safeJSONStringify({assets, groups, types, categories})}`;
};

/**
 * Resolves the key a count of the given filters is kept under - shared with
 * the Time Mode control, so the count it takes off the map under the applied
 * filters is only ever taken up for those same filters.
 *
 * ```typescript
 * import {getTrackingFilterCountKey} from '@/components/filter/current/useTrackingFilterLimitWarning';
 *
 * ...
 *
 * const key = getTrackingFilterCountKey(globalGroupId, filters);
 * ```
 *
 * @param globalGroupId - the global group the count is taken under
 * @param filters       - the filters counted, in the shape they are applied in
 * @returns The key the count is kept under.
 */
export const getTrackingFilterCountKey = (globalGroupId: number | string, filters: GenericObject): string => (
  toCountKey(globalGroupId, getReportParams({filters} as any)?.params)
);

/**
 * Defines the inputs the Tracking Filter's limit warning is checked under.
 */
export interface UseTrackingFilterLimitWarningArgs {
  // whether the drawer is open - nothing is counted while it is closed ...
  open: boolean;
  // the drawer's drafted selection, in the shape the filters are applied in ...
  draftState: TrackingFavoriteFilterState;
}

/**
 * Defines what the Tracking Filter's limit check reports.
 */
export interface TrackingFilterLimitWarningResult {
  // the warning's figures while the draft is over the limit, otherwise `null` ...
  limitWarning: TimeModeLimitWarningProps | null;
  // whether a count for the latest draft is still under way ...
  isCheckingLimit: boolean;
  // checks the latest draft at once, without waiting for it to settle, and
  // resolves to its warning - what Apply asks before applying a draft whose
  // count is still under way ...
  checkLimitNow: () => Promise<TimeModeLimitWarningProps | null>;
}

/**
 * Checks the Tracking Filter's drafted selection against the running
 * Timeline or History report's limit, so the drawer can warn before the
 * selection is applied.
 * - Only runs while the drawer is open and a Timeline or History report is
 *   actually running; otherwise there is no warning.
 * - The draft the drawer opens on is counted at once. Each time the drafted
 *   Assets, Groups, Categories or Asset Types change after that, the backend
 *   is asked how many assets they select - counted the way the History
 *   report's own request counts them - once the draft has settled.
 * - A run of quick changes costs one count, and only the latest draft's
 *   count answers: one the draft has since moved past is called off, and
 *   any answer it still gives is let go.
 * - Each draft's count is kept while the report runs, so returning to a
 *   draft already counted - or opening the drawer again - answers at once.
 *   The drawer opening on the filters the Time Mode control last counted off
 *   the map takes that count up too, so it warns without asking again.
 * - The count, over the report's days, is checked against the Time Mode
 *   limit the "{{mode}} wasn't updated" card reports. A count that fails
 *   lets the selection through rather than holding Apply back.
 *
 * ```typescript
 * import useTrackingFilterLimitWarning from './useTrackingFilterLimitWarning';
 *
 * ...
 *
 * const {limitWarning, isCheckingLimit, checkLimitNow} = useTrackingFilterLimitWarning({
 *   open,
 *   draftState
 * });
 * ```
 *
 * @param open       - whether the drawer is open
 * @param draftState - the drawer's drafted selection
 * @returns The warning's figures while the draft is over the limit, otherwise
 *          `null`, whether the latest draft's count is still under way, and a
 *          check that answers for the latest draft at once.
 */
export const useTrackingFilterLimitWarning = ({
  open,
  draftState
}: UseTrackingFilterLimitWarningArgs): TrackingFilterLimitWarningResult => {
  const timeMode            = useSelector(timeModeSelector);
  const trackingMode        = useSelector(trackingModeSelector);
  const isHistoryActive     = useSelector(historyActiveSelector);
  const isLiveHistoryActive = useSelector(liveHistoryActiveSelector);
  const historyFrom         = useSelector(historyFromSelector);
  const historyTo           = useSelector(historyToSelector);
  const boundaryMode        = useSelector(boundaryModeSelector);
  const boundary            = useSelector(boundaryFilterSelector);
  const globalGroup         = useSelector(globalGroupStateSelector);
  const globalGroupId       = useSelector(globalGroupIdSelector);
  const [limitWarning, setLimitWarning]       = useState<TimeModeLimitWarningProps | null>(null);
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  // only the latest count may answer - an earlier one still in flight when the
  // draft changes again is called off, and any answer it still gives let go ...
  const requestRef = useRef(0);
  // the count waiting to start, and the request already asked of the backend,
  // so a check asked for at once can call off the one it overtakes ...
  const timerRef  = useRef<ReturnType<typeof setTimeout>>();
  const sourceRef = useRef<CancelTokenSource>();
  // each draft's count while the report runs, keyed by what it selects ...
  const countsRef = useRef(new Map<string, number>());
  // whether the draft has been counted since the drawer opened - the first
  // count goes at once, those after it wait for the draft to settle ...
  const hasCountedRef = useRef(false);

  const runningReport = getRunningTimeModeReport({
    timeMode,
    trackingMode,
    isHistoryActive,
    isLiveHistoryActive
  });
  const days = runningReport === TimeModes.timeline
    ? TIMELINE_LIMIT_DAYS
    : toRangeDays(historyFrom as Date, historyTo as Date);
  // a History report over a vicinity search is bounded by the vicinity
  // rather than by the fleet the draft selects, so it is never held to the
  // limit - in the current theme, while its Vicinity Search chip stands ...
  const isVicinityHistory = isVicinityHistoryReport({runningReport, boundaryMode, boundary});
  const isChecking        = open && !!runningReport && !isVicinityHistory;

  // the draft as the report's own params - only the Assets, Groups,
  // Categories and Asset Types it selects reach them, so a change to any
  // other category leaves them, and the count, as they were ...
  const params   = useMemo(() => getReportParams({filters: draftState} as any)?.params, [draftState]);
  const countKey = toCountKey(globalGroupId, params);

  // a count only answers for the report it was taken under ...
  useEffect(() => {
    countsRef.current.clear();
  }, [runningReport, globalGroupId]);

  const toWarning = useCallback((assets: number): TimeModeLimitWarningProps | null => (
    runningReport && assets * days > TIME_MODE_ASSET_DAY_LIMIT
      ? {mode: runningReport, assets, days, max: TIME_MODE_ASSET_DAY_LIMIT}
      : null
  ), [runningReport, days]);

  // calls off whatever count is waiting or under way ...
  const cancelPending = useCallback(() => {
    clearTimeout(timerRef.current);
    sourceRef.current?.cancel();
  }, []);

  // answers for the latest draft - from its kept count where there is one,
  // otherwise from the backend, keeping what it says. An answer is only
  // taken up while it is still the latest one asked for ...
  const check = useCallback((request: number): Promise<TimeModeLimitWarningProps | null> => {
    const source = axios.CancelToken.source();
    const kept   = countsRef.current.get(countKey);
    sourceRef.current = source;
    return (kept !== undefined
      ? Promise.resolve(toWarning(kept))
      : getTrackingFilterAssetCount({params, globalGroup, globalGroupId, cancelToken: source.token})
        .then(function(assets) {
          countsRef.current.set(countKey, assets);
          return toWarning(assets);
        })
        .catch(function() {
          return null;
        })
    ).then(function(next) {
      if (request === requestRef.current) {
        setLimitWarning(next);
        setIsCheckingLimit(false);
      }
      return next;
    });
  }, [countKey, params, globalGroup, globalGroupId, toWarning]);

  useEffect(() => {
    const request    = ++requestRef.current;
    const layerCount = getTimeModeLayerCount();
    if (isChecking) {
      // the map's own count of the applied filters, where the draft is still
      // those filters - what the "{{mode}} wasn't updated" card reported ...
      if (layerCount?.key === countKey && !countsRef.current.has(countKey)) {
        countsRef.current.set(countKey, layerCount.assets);
      }
      if (countsRef.current.has(countKey)) {
        check(request);
      } else {
        setIsCheckingLimit(true);
        timerRef.current = setTimeout(function() {
          check(request);
        }, hasCountedRef.current ? LIMIT_CHECK_DEBOUNCE : 0);
      }
      hasCountedRef.current = true;
    } else {
      hasCountedRef.current = false;
      setLimitWarning(null);
      setIsCheckingLimit(false);
    }
    // a draft that moves on - or a drawer that closes - calls off both the
    // count still waiting to start and the one already asked of the backend ...
    return cancelPending;
  }, [isChecking, countKey, check]);

  const checkLimitNow = useCallback(() => {
    let result: Promise<TimeModeLimitWarningProps | null> = Promise.resolve(null);
    if (isChecking) {
      cancelPending();
      result = check(++requestRef.current);
    }
    return result;
  }, [isChecking, cancelPending, check]);

  return {limitWarning, isCheckingLimit, checkLimitNow};
};

export default useTrackingFilterLimitWarning;
